import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThreeDots } from 'react-loader-spinner';
import ReactPaginate from 'react-paginate';
import moment from 'moment';
import { Dialog, DialogTitle, Slider } from '@mui/material';
import { toast } from 'react-toastify';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { API_BASE_URL } from '../../config/api';

export default function ActiveProjects() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [count, setCount] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [jobStatus, setJobStatus] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [projectId, setProjectId] = useState(false);

    const fetchData = async () => {
        setLoading(true);

        const url = API_BASE_URL;
        const token = localStorage.getItem('authToken');
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };

        try {
            const response = await fetch(
                url + `/sub_cont_active_project?page_num_start=1&page_size=20`,
                requestOptions
            );

            if (response.ok) {
                const data = await response.json();

                if (data?.data?.projects.length > 0) {
                    setCount(Math.ceil(data.data.projectsCount / 10));
                    setPageCount(data.data.projectsCount);
                    setProjects(data.data.projects);
                } else {
                    setError(true);
                }
            } else {
                setError(true);
            }
            setLoading(false);
        } catch (error) {
            setError(true);
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        setTimeout(() => {
            fetchData();
        }, 1000);
    }, [jobStatus]);

    const fetchPaginatedData = async (currentPage) => {
        const url = API_BASE_URL;
        setLoading(true);
        const token = localStorage.getItem('authToken');
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };
        const res = await fetch(
            url + `/sub_cont_active_project?page_num_start=${currentPage}&page_size=20`,
            requestOptions
        );
        const data = await res.json();
        setLoading(false);
        return data;
    };

    const handlePageClick = async (data) => {
        const currentPage = data.selected + 1;
        const dataFromServer = await fetchPaginatedData(currentPage);
        setProjects(dataFromServer?.data?.projects);
    };

    const handleSliderChange = async (newValue, project) => {
        setProjectId(project.id);

        if (newValue === 100) {
            setJobStatus(100);
            setModalOpen(true);
            return;
        }

        // Update local state immediately for responsive UI
        setProjects(prevProjects => 
            prevProjects.map(p => 
                p.id === project.id ? { ...p, job_status: newValue } : p
            )
        );

        try {
            const url = API_BASE_URL;
            const token = localStorage.getItem('authToken');

            const requestData = {
                job_status: newValue,
            };

            const requestOptions = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(requestData),
            };

            const res = await fetch(
                url + `/update_project_status/${project.id}`,
                requestOptions
            );

            if (!res.ok) {
                const errorText = await res.text();
                console.error('API Error:', res.status, errorText);
                toast.error('Failed to update status', { autoClose: 3000 });
                fetchData();
                return;
            }

            const body = await res.json();

            if (body.success === true) {
                toast.success('Job status updated successfully!', { autoClose: 3000 });
            } else {
                toast.error(body.message || 'Something went wrong', { autoClose: 3000 });
                // Revert on failure
                fetchData();
            }
        } catch (err) {
            console.error('Request failed:', err);
            toast.error('Something went wrong', { autoClose: 3000 });
            fetchData();
        }
    };

    const handleSubmit = async () => {
        try {
            const url = API_BASE_URL;
            const token = localStorage.getItem('authToken');

            const requestData = {
                job_status: 100,
            };

            const requestOptions = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(requestData),
            };

            const res = await fetch(
                url + `/project_complete_status/${projectId}`,
                requestOptions
            );

            const body = await res.json();

            if (body.success === true) {
                toast.success('Job Completed successfully!', { autoClose: 3000 });
            } else {
                toast.error('Something went wrong', { autoClose: 3000 });
            }
            setModalOpen(false);
            setJobStatus('');
        } catch (err) {
            console.error(err);
        }
    };

    const handleClose = () => {
        setJobStatus('');
        setModalOpen(false);
    };

    const getProgressColor = (status) => {
        if (status === 'completed' || status >= 100) return '#4caf50';
        if (status >= 70) return '#8bc34a';
        if (status >= 40) return '#c9a227';
        return '#ff9800';
    };

    const sliderMarks = [
        { value: 0, label: '0%' },
        { value: 50, label: '50%' },
        { value: 100, label: '100%' },
    ];

    return (
        <div className="profile_page">
            <div className="container">
                <div className="profile_section">
                    <div className="section_header">
                        <div className="section_header_row">
                            <div>
                                <h3>Active Projects</h3>
                                <p>{pageCount} active project{pageCount !== 1 ? 's' : ''}</p>
                            </div>
                        </div>
                    </div>
                    <div className="section_content">
                        {loading ? (
                            <div className="loading_state">
                                <ThreeDots
                                    height="60"
                                    width="80"
                                    radius="9"
                                    color="#B1903F"
                                    ariaLabel="three-dots-loading"
                                    visible={true}
                                />
                                <p>Loading active projects...</p>
                            </div>
                        ) : projects.length === 0 ? (
                            <div className="empty_state">
                                <WorkOutlineIcon className="empty_icon" />
                                <h4>No Active Projects</h4>
                                <p>You don't have any active projects yet. Once you get hired for a project, it will appear here.</p>
                            </div>
                        ) : (
                            <>
                                <div className="projects_grid">
                                    {projects.map((project, index) => (
                                        <div 
                                            key={index} 
                                            className="project_card"
                                            onClick={() => navigate(`/project-details/${project.id}/true`)}
                                        >
                                            <div className="project_card_header">
                                                <h3 className="project_title">
                                                    {project.project_name.length > 60 
                                                        ? `${project.project_name.slice(0, 60)}...` 
                                                        : project.project_name}
                                                </h3>
                                                <span className={`project_status_badge ${project.status === 'completed' ? 'completed' : 'active'}`}>
                                                    {project.status === 'completed' ? (
                                                        <><CheckCircleIcon /> Completed</>
                                                    ) : (
                                                        'Active'
                                                    )}
                                                </span>
                                            </div>
                                            
                                            <div className="project_card_meta">
                                                {project?.award_bid !== null && (
                                                    <span className="meta_item">
                                                        <CalendarTodayIcon />
                                                        Awarded {moment(project?.award_bid?.awarded_date).format("MMM DD, YYYY")}
                                                    </span>
                                                )}
                                            </div>
                                            
                                            <p className="project_description">
                                                {project.project_description.length > 150 
                                                    ? `${project.project_description.slice(0, 150)}...` 
                                                    : project.project_description}
                                            </p>

                                            {project?.award_bid !== null && (
                                                <div className="project_progress_section" onClick={(e) => e.stopPropagation()}>
                                                    <div className="progress_header">
                                                        <span className="progress_label">
                                                            <TrendingUpIcon /> Job Progress
                                                        </span>
                                                        <span 
                                                            className="progress_value"
                                                            style={{ color: getProgressColor(project.status === 'completed' ? 100 : project.job_status) }}
                                                        >
                                                            {project.status === 'completed' ? '100' : (project.job_status || 0)}%
                                                        </span>
                                                    </div>
                                                    <div className="progress_bar_container">
                                                        <div 
                                                            className="progress_bar_fill"
                                                            style={{ 
                                                                width: `${project.status === 'completed' ? 100 : (project.job_status || 0)}%`,
                                                                backgroundColor: getProgressColor(project.status === 'completed' ? 100 : project.job_status)
                                                            }}
                                                        />
                                                    </div>
                                                    {project.status !== 'completed' && (
                                                        <div className="status_slider_container">
                                                            <span className="slider_label">Update Status</span>
                                                            <Slider
                                                                value={project.job_status || 0}
                                                                onChange={(e, newValue) => {
                                                                    // Update UI immediately while dragging
                                                                    setProjects(prevProjects => 
                                                                        prevProjects.map(p => 
                                                                            p.id === project.id ? { ...p, job_status: newValue } : p
                                                                        )
                                                                    );
                                                                }}
                                                                onChangeCommitted={(e, newValue) => handleSliderChange(newValue, project)}
                                                                step={10}
                                                                marks={sliderMarks}
                                                                min={0}
                                                                max={100}
                                                                valueLabelDisplay="auto"
                                                                valueLabelFormat={(value) => `${value}%`}
                                                                sx={{
                                                                    color: getProgressColor(project.job_status || 0),
                                                                    '& .MuiSlider-thumb': {
                                                                        backgroundColor: getProgressColor(project.job_status || 0),
                                                                    },
                                                                    '& .MuiSlider-track': {
                                                                        backgroundColor: getProgressColor(project.job_status || 0),
                                                                    },
                                                                    '& .MuiSlider-rail': {
                                                                        backgroundColor: '#e0e0e0',
                                                                    },
                                                                    '& .MuiSlider-mark': {
                                                                        backgroundColor: '#bfbfbf',
                                                                    },
                                                                    '& .MuiSlider-markLabel': {
                                                                        fontSize: '11px',
                                                                        color: '#888',
                                                                    },
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {pageCount > 10 && (
                                    <div className="pagination_wrapper">
                                        <ReactPaginate
                                            previousLabel={"Prev"}
                                            nextLabel={"Next"}
                                            breakLabel={"..."}
                                            pageCount={count}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageClick}
                                            containerClassName={"pagination"}
                                            pageClassName={"page-item"}
                                            pageLinkClassName={"item-link"}
                                            previousClassName={"page-item"}
                                            previousLinkClassName={"item-link"}
                                            nextClassName={"page-item"}
                                            nextLinkClassName={"item-link"}
                                            breakClassName={"page-item"}
                                            breakLinkClassName={"item-link"}
                                            activeClassName={"active"}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="profile_actions centered">
                    <div className="search_input_wrapper large">
                        <SearchIcon className="search_icon" />
                        <input
                            type="text"
                            placeholder="Find a Project"
                            className="contacts_search_input"
                            onClick={() => navigate('/find-a-project')}
                            readOnly
                        />
                    </div>
                </div>
            </div>

            {modalOpen && (
                <Dialog open={modalOpen} onClose={handleClose}>
                    <div className="delete_dialog">
                        <DialogTitle className="delete_dialog_title">
                            Complete Confirmation
                        </DialogTitle>
                        <div className="delete_dialog_content">
                            <p>Are you sure you want to mark this project as complete?</p>
                            <p className="delete_warning">This action cannot be undone.</p>
                        </div>
                        <div className="delete_dialog_actions">
                            <button onClick={handleClose} className="cancel_btn">
                                Cancel
                            </button>
                            <button onClick={handleSubmit} className="save_btn">
                                Confirm Complete
                            </button>
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
}
